import { useState } from 'react'
import { Dialog, DialogContent, Box, Typography, IconButton, CircularProgress } from '@mui/material'
import { Close, Rocket } from '@mui/icons-material'
import { designSpecs } from '../../theme/designSpecs'
import Button from '../common/Button'
import subscriptionService from '../../services/subscriptionService'

const SubscriptionModal = ({ open, onClose, uploadedFile, prompt }) => {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  const handleGetSubscription = async () => {
    setLoading(true)
    setError('')

    try {
      // Save current state to sessionStorage before redirect
      if (uploadedFile && prompt) {
        // Convert file to base64 for storage
        const reader = new FileReader()
        reader.onloadend = () => {
          sessionStorage.setItem(
            'pending_generation',
            JSON.stringify({
              imageDataUrl: reader.result,
              fileName: uploadedFile.name,
              fileType: uploadedFile.type,
              prompt: prompt,
              timestamp: Date.now(),
            })
          )
        }
        reader.readAsDataURL(uploadedFile)
      }

      // Redirect to Stripe checkout
      await subscriptionService.redirectToCheckout()
      // If successful, user will be redirected to Stripe
      // Backend handles redirect back after payment
    } catch (err) {
      console.error('Checkout error:', err)
      setError(err.message || 'Failed to start checkout process. Please try again.')
      setLoading(false)
    }
  }

  const handleModalClose = () => {
    if (!loading) {
      setError('')
      onClose()
    }
  }

  return (
    <Dialog
      open={open}
      onClose={handleModalClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: designSpecs.borderRadius['2xl'],
          p: 2,
          maxWidth: 480,
        },
      }}
    >
      <Box sx={{ position: 'relative' }}>
        <IconButton
          onClick={handleModalClose}
          disabled={loading}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: 'grey.500',
            '&:hover': {
              bgcolor: 'grey.100',
            },
            '&.Mui-disabled': {
              color: 'grey.300',
            },
          }}
        >
          <Close />
        </IconButton>

        <DialogContent sx={{ px: 4, py: 3 }}>
          <Box sx={{ textAlign: 'center' }}>
            {/* Icon */}
            <Box
              sx={{
                width: 64,
                height: 64,
                background: designSpecs.gradients.primary,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mx: 'auto',
                mb: 3,
              }}
            >
              <Rocket sx={{ color: 'white', fontSize: 32 }} />
            </Box>

            {/* Heading */}
            <Typography
              variant="h4"
              sx={{
                mb: 2,
                fontWeight: 700,
                color: 'grey.900',
              }}
            >
              Subscribe to Generate Images
            </Typography>

            {/* Description */}
            <Typography
              variant="body1"
              sx={{
                mb: 4,
                color: 'grey.600',
                lineHeight: 1.6,
              }}
            >
              Unlock unlimited AI-powered image generation for your products. Create professional
              mockups and variations with just a few clicks.
            </Typography>

            {/* Error Message */}
            {error && (
              <Box
                sx={{
                  mb: 3,
                  p: 2,
                  bgcolor: 'error.50',
                  borderRadius: designSpecs.borderRadius.lg,
                  border: '1px solid',
                  borderColor: 'error.200',
                }}
              >
                <Typography variant="body2" sx={{ color: 'error.700' }}>
                  {error}
                </Typography>
              </Box>
            )}

            {/* Get Subscription Button */}
            <Button
              fullWidth
              variant="contained"
              size="large"
              onClick={handleGetSubscription}
              disabled={loading}
              sx={{
                py: 1.5,
                background: designSpecs.gradients.primary,
                color: 'white',
                fontWeight: 600,
                fontSize: designSpecs.typography.sizes.base,
                '&:hover': {
                  background: designSpecs.gradients.primary,
                  opacity: 0.9,
                },
                '&.Mui-disabled': {
                  background: 'grey.200',
                  color: 'grey.500',
                },
              }}
            >
              {loading ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={20} sx={{ color: 'white' }} />
                  <span>Redirecting to checkout...</span>
                </Box>
              ) : (
                'Get Subscription'
              )}
            </Button>

            {/* Cancel Link */}
            {!loading && (
              <Typography
                variant="body2"
                sx={{
                  mt: 2,
                  color: 'grey.500',
                  cursor: 'pointer',
                  '&:hover': {
                    color: 'grey.700',
                  },
                }}
                onClick={handleModalClose}
              >
                Maybe later
              </Typography>
            )}
          </Box>
        </DialogContent>
      </Box>
    </Dialog>
  )
}

export default SubscriptionModal
